import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { throwError, TimeoutError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

/**
 * CIC-01 (junta Ingenieria 27-ago-2026): al guardar un ciclo, con la demo
 * lenta el navegador se quedaba "pegado" 4-5 min hasta que el propio socket
 * expiraba solo — el registro SI se grababa en base, pero sin confirmacion
 * visible el usuario creia que fallo y volvia a capturar (riesgo de
 * duplicados). Se acorta el timeout a algo razonable y, si se cumple, se
 * distingue con un mensaje explicito que le dice al usuario que verifique el
 * listado antes de reintentar, en vez de dejar la pantalla congelada.
 * Solo aplica a verbos de escritura: GET (listados/export) puede tardar mas
 * con datasets grandes y no debe cortarse.
 */
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const SAVE_TIMEOUT_MS = 15000;

/**
 * Hay procesos que no son un alta: recalculan o generan informacion de toda la
 * plantilla y tardan minutos, no segundos. Cortarlos a los 15 s le ensena al
 * usuario un error por tiempo cuando el servidor sigue trabajando bien. Esas
 * llamadas declaran aqui su propio limite y el interceptor lo respeta; el resto
 * de los guardados se queda con el limite corto de siempre.
 */
export const TIMEOUT_MS = new HttpContextToken<number>(() => SAVE_TIMEOUT_MS);

/** Distingue el error sintético de este interceptor de un error real del backend. */
export function isSaveTimeoutError(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === 0 && err.statusText === 'Timeout';
}

export const saveTimeoutInterceptor: HttpInterceptorFn = (req, next) => {
  if (!WRITE_METHODS.has(req.method)) return next(req);

  return next(req).pipe(
    timeout(req.context.get(TIMEOUT_MS)),
    catchError(err => {
      if (err instanceof TimeoutError) {
        const timeoutError = new HttpErrorResponse({
          error: {
            message:
              'No se recibió confirmación del servidor a tiempo. El registro pudo haberse guardado: ' +
              'revisa el listado antes de volver a capturarlo.',
          },
          status: 0,
          statusText: 'Timeout',
          url: req.url,
        });
        return throwError(() => timeoutError);
      }
      return throwError(() => err);
    }),
  );
};
